import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { UserFocus, PlayCircle, PauseCircle, PaperPlaneTilt, UserGear, X, CheckCircle } from "@phosphor-icons/react";

const ACTIONS = ["FOLLOW_UP", "CHECKOUT_RESCUE", "PRODUCT_INTEREST_FOLLOWUP", "ONBOARDING",
                 "CROSS_SELL", "UPSELL", "RENEWAL", "RETENTION", "WINBACK", "REFERRAL"];

export default function AdminJourneys() {
  const [rows, setRows] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/ai-outreach/journeys"); setRows(data.journeys); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openDetail = async (uid) => {
    try { const { data } = await api.get(`/ai-outreach/journey/${uid}`); setDetail(data); }
    catch (e) { toast.error(apiError(e)); }
  };

  const act = async (fn, ok) => { setBusy(true); try { await fn(); toast.success(ok); await load(); if (detail) await openDetail(detail.journey.user_id); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };
  const toggleAuto = (r) => act(() => api.post(`/ai-outreach/journey/${r.user_id}/automation`, { paused: !r.automation_paused }), r.automation_paused ? "Automation resumed" : "Automation paused");
  const escalate = (uid) => act(() => api.post(`/ai-outreach/journey/${uid}/escalate`, {}), "Escalated to a human");
  const trigger = async (uid, action_type) => {
    setBusy(true);
    try { const { data } = await api.post(`/ai-outreach/journey/${uid}/trigger`, { action_type });
      toast[data.status === "sent" ? "success" : "message"](`Action ${action_type}: ${data.status}${data.reason ? " · " + data.reason : ""}`);
      await load(); if (detail) await openDetail(uid);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!rows) return <div className="py-16 text-center text-muted-foreground" data-testid="journeys-loading">Loading journeys…</div>;

  return (
    <div className="space-y-6" data-testid="admin-journeys">
      <div>
        <h1 className="flex items-center gap-2 font-display text-3xl font-semibold tracking-tight"><UserFocus size={28} weight="fill" /> Member Journeys</h1>
        <p className="mt-1 text-sm text-muted-foreground">Every lead's live sales journey. Trigger the next best action, pause automation or hand off to a human — all still pass the consent & safety gate.</p>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-white/10 bg-card">
        <table className="w-full text-sm" data-testid="journeys-table">
          <thead><tr className="text-left text-xs uppercase text-muted-foreground">
            <th className="p-3">Member</th><th>Stage</th><th>Score</th><th>Next action</th><th>When</th><th>Status</th><th>Consent</th><th className="pr-3 text-right">Controls</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.user_id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`journey-row-${r.user_id}`}>
                <td className="p-3">
                  <button onClick={() => openDetail(r.user_id)} className="font-medium hover:text-primary" data-testid={`journey-open-${r.user_id}`}>{r.name || r.email || r.user_id.slice(-6)}</button>
                  <p className="text-xs text-muted-foreground">{r.email}</p>
                </td>
                <td className="capitalize">{r.sales_stage || "—"}</td>
                <td>{r.lead_score ?? "—"}</td>
                <td>{(r.next_action_type || "—").replace(/_/g, " ")}</td>
                <td className="text-xs text-muted-foreground">{(r.next_action_at || "").slice(0, 16).replace("T", " ") || "—"}</td>
                <td>{r.automation_paused ? <span className="text-amber-500">paused</span> : <span className="text-muted-foreground">{r.follow_up_status || "idle"}</span>}</td>
                <td className="text-xs">{Object.entries(r.consent || {}).filter(([, v]) => v).map(([k]) => k).join(", ") || "—"}</td>
                <td className="pr-3">
                  <div className="flex items-center justify-end gap-1">
                    <button data-testid={`journey-auto-${r.user_id}`} disabled={busy} onClick={() => toggleAuto(r)} title={r.automation_paused ? "Resume automation" : "Pause automation"}
                      className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-white/10">
                      {r.automation_paused ? <PlayCircle size={18} weight="fill" className="text-emerald-500" /> : <PauseCircle size={18} weight="fill" className="text-amber-500" />}
                    </button>
                    <button data-testid={`journey-escalate-${r.user_id}`} disabled={busy} onClick={() => escalate(r.user_id)} title="Escalate to human"
                      className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-white/10"><UserGear size={17} /></button>
                    <button data-testid={`journey-detail-${r.user_id}`} disabled={busy} onClick={() => openDetail(r.user_id)} title="Open journey"
                      className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-white/10"><PaperPlaneTilt size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">No member journeys yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setDetail(null)}>
          <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-white/10 bg-card p-5" onClick={(e) => e.stopPropagation()} data-testid="journey-detail">
            <div className="mb-4 flex items-start justify-between">
              <div>
                <h2 className="font-display text-xl font-semibold">{detail.journey.name || detail.journey.email}</h2>
                <p className="text-sm text-muted-foreground">{detail.journey.email}</p>
              </div>
              <button onClick={() => setDetail(null)} className="rounded-full p-1 hover:bg-white/10"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Info label="Stage" v={detail.journey.sales_stage} />
              <Info label="Lead score" v={detail.journey.lead_score} />
              <Info label="Next action" v={(detail.journey.next_action_type || "—").replace(/_/g, " ")} />
              <Info label="Follow-up" v={detail.journey.automation_paused ? "paused" : detail.journey.follow_up_status} />
              <Info label="Last purchase" v={(detail.journey.last_purchase_at || "—").slice(0, 10)} />
              <Info label="Last touch" v={(detail.journey.last_interaction_at || "—").slice(0, 16).replace("T", " ")} />
            </div>
            {detail.journey.interests?.length > 0 && <p className="mt-3 text-xs text-muted-foreground">Interests: {detail.journey.interests.join(", ")}</p>}

            <div className="mt-5">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Trigger next best action</p>
              <div className="flex flex-wrap gap-2">
                {ACTIONS.map((a) => (
                  <button key={a} data-testid={`journey-trigger-${a}`} disabled={busy} onClick={() => trigger(detail.journey.user_id, a)}
                    className="rounded-full border border-white/10 px-3 py-1.5 text-xs font-medium capitalize hover:border-primary disabled:opacity-50">{a.replace(/_/g, " ")}</button>
                ))}
              </div>
            </div>

            <div className="mt-5">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Outreach history</p>
              <div className="space-y-1.5">
                {(detail.outreach || []).slice(0, 12).map((o) => (
                  <div key={o._id} className="flex items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2 text-xs">
                    <span>{(o.action_type || "").replace(/_/g, " ")} · {o.channel || "—"}</span>
                    <span className={o.status === "sent" ? "text-emerald-500" : o.status === "failed" ? "text-destructive" : "text-muted-foreground"}>
                      {o.converted ? <span className="inline-flex items-center gap-1 text-emerald-500"><CheckCircle size={12} weight="fill" /> converted</span> : o.status}
                    </span>
                  </div>
                ))}
                {(!detail.outreach || detail.outreach.length === 0) && <p className="text-xs text-muted-foreground">No outreach yet.</p>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ label, v }) {
  return <div className="rounded-xl bg-white/[0.03] px-3 py-2"><p className="text-[10px] uppercase text-muted-foreground">{label}</p><p className="font-medium capitalize">{v ?? "—"}</p></div>;
}
